import { dateFilterOptions } from '@/config';
import { LIMIT } from '@/constants';
import {
  fetchDistributorBindingCustomers,
  fetchDistributorBindingMembers,
  fetchDistributorShareProducts,
  fetchDistributorsPromoteOrders,
} from '@/services';
import {
  DistributorCenterCustomerType,
  DistributorCenterMemberStateInput,
  DistributorCenterPageKey,
  LoadMoreStatus,
} from '@/types';
import { defineStore } from 'pinia';

interface State {
  /** 推广订单列表数据配置 */
  orderConfig: {
    list: AnyObject[];
    total: number;
    /** 列表加载状态 */
    loadStatus: LoadMoreStatus;
    /** 列表请求选项 */
    options: {
      customerId: string;
      search: string;
      startDateTime: Date;
      endDateTime: Date;
      skip: number;
    };
  };
  /** 推广商品列表数据配置 */
  productConfig: {
    list: AnyObject[];
    total: number;
    /** 列表加载状态 */
    loadStatus: LoadMoreStatus;
    /** 列表请求选项 */
    options: {
      search: string;
      startDateTime: Date;
      endDateTime: Date;
      skip: number;
    };
  };
  /** 分销用户列表数据配置 */
  customerConfig: {
    list: AnyObject[];
    total: number;
    /** 列表加载状态 */
    loadStatus: LoadMoreStatus;
    /** 列表请求选项 */
    options: {
      customerType: DistributorCenterCustomerType;
      search: string;
      startDateTime: Date;
      endDateTime: Date;
      skip: number;
    };
  };
  /** 开卡用户列表数据配置 */
  purchaseCardCustomerConfig: {
    list: AnyObject[];
    total: number;
    /** 列表加载状态 */
    loadStatus: LoadMoreStatus;
    /** 列表请求选项 */
    options: {
      search: string;
      startDateTime: Date;
      endDateTime: Date;
      state: DistributorCenterMemberStateInput;
      skip: number;
    };
  };
  /** 搜索接口列表数据配置 */
  searchResultConfig: {
    list: AnyObject[];
    /** 列表加载状态 */
    loadStatus: LoadMoreStatus;
    /** 列表请求选项 */
    options: {
      customerType?: DistributorCenterCustomerType;
      state?: DistributorCenterMemberStateInput;
      customerId?: string;
      search: string;
      startDateTime: Date;
      endDateTime: Date;
      skip: number;
    };
  } | null;
  /** 客户详情的订单列表数据 */
  customerDetailOrderList: AnyObject[];
}

export const useDistributorCenterStore = defineStore('distributorCenter', {
  state: (): State => ({
    orderConfig: {
      list: [],
      total: 0,
      loadStatus: LoadMoreStatus.loading,
      options: {
        customerId: '',
        search: '',
        startDateTime: new Date(dateFilterOptions.today.startDateTime),
        endDateTime: new Date(dateFilterOptions.today.endDateTime),
        skip: 0,
      },
    },
    productConfig: {
      list: [],
      total: 0,
      loadStatus: LoadMoreStatus.loading,
      options: {
        search: '',
        startDateTime: new Date(dateFilterOptions.today.startDateTime),
        endDateTime: new Date(dateFilterOptions.today.endDateTime),
        skip: 0,
      },
    },
    customerConfig: {
      list: [],
      total: 0,
      loadStatus: LoadMoreStatus.loading,
      options: {
        customerType: DistributorCenterCustomerType.allCustomer,
        search: '',
        startDateTime: new Date(dateFilterOptions.today.startDateTime),
        endDateTime: new Date(dateFilterOptions.today.endDateTime),
        skip: 0,
      },
    },
    purchaseCardCustomerConfig: {
      list: [],
      total: 0,
      loadStatus: LoadMoreStatus.loading,
      options: {
        search: '',
        startDateTime: new Date(dateFilterOptions.today.startDateTime),
        endDateTime: new Date(dateFilterOptions.today.endDateTime),
        state: DistributorCenterMemberStateInput.all,
        skip: 0,
      },
    },
    searchResultConfig: null,
    customerDetailOrderList: [],
  }),

  getters: {},

  actions: {
    async getOrderList(
      {
        customerId = '',
        search = '',
        startDateTime = new Date(0),
        endDateTime = new Date(),
        skip = 0,
      },
      refresh?: boolean
    ) {
      const variables = {
        customerId,
        search,
        startDateTime,
        endDateTime,
        options: {
          skip,
        },
      };
      const res = await fetchDistributorsPromoteOrders(variables);
      if (res?.items) {
        this.orderConfig.total = res.totalItems;

        if (res.items.length < LIMIT) {
          this.orderConfig.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.orderConfig.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.orderConfig.list = res.items;
        } else {
          this.orderConfig.list = [...this.orderConfig.list, ...res.items];
        }
      }
      return this.orderConfig.list;
    },
    async getProductList(
      { search = '', startDateTime = new Date(0), endDateTime = new Date(), skip = 0 },
      refresh?: boolean
    ) {
      const variables = {
        search,
        startDateTime,
        endDateTime,
        options: {
          skip,
        },
      };
      const res = await fetchDistributorShareProducts(variables);
      if (res?.items) {
        this.productConfig.total = res.totalItems;

        if (res.items.length < LIMIT) {
          this.productConfig.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.productConfig.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.productConfig.list = res.items;
        } else {
          this.productConfig.list = [...this.productConfig.list, ...res.items];
        }
      }
      return this.productConfig.list;
    },
    async getCustomerList(
      {
        customerType = DistributorCenterCustomerType.allCustomer,
        search = '',
        startDateTime = new Date(0),
        endDateTime = new Date(),
        skip = 0,
      },
      refresh?: boolean
    ) {
      const variables = {
        customerType,
        search,
        startDateTime,
        endDateTime,
        options: {
          skip,
        },
      };
      const res = await fetchDistributorBindingCustomers(variables);
      if (res?.items) {
        this.customerConfig.total = res.totalItems;

        if (res.items.length < LIMIT) {
          this.customerConfig.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.customerConfig.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.customerConfig.list = res.items;
        } else {
          this.customerConfig.list = [...this.customerConfig.list, ...res.items];
        }
      }
      return this.customerConfig.list;
    },
    async getPurchaseCardCustomerList(
      {
        search = '',
        startDateTime = new Date(0),
        endDateTime = new Date(),
        state = DistributorCenterMemberStateInput.normal,
        skip = 0,
      },
      refresh?: boolean
    ) {
      const variables = {
        search,
        startDateTime,
        endDateTime,
        state,
        skip: LIMIT * skip,
      };
      const res = await fetchDistributorBindingMembers(variables);

      if (res?.items) {
        this.purchaseCardCustomerConfig.total = res.totalItems;

        if (res.items.length < LIMIT) {
          this.purchaseCardCustomerConfig.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.purchaseCardCustomerConfig.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.purchaseCardCustomerConfig.list = res.items;
        } else {
          this.purchaseCardCustomerConfig.list = [
            ...this.purchaseCardCustomerConfig.list,
            ...res.items,
          ];
        }
      }

      console.log(`this.purchaseCardCustomerConfig`, this.purchaseCardCustomerConfig);

      return this.purchaseCardCustomerConfig.list;
    },
    async getSearchResultList(
      pageKey: DistributorCenterPageKey,
      variables: AnyObject,
      refresh?: boolean
    ) {
      const _variables: AnyObject = {
        ...variables,
        options: {
          skip: variables.skip,
        },
      };
      delete _variables.skip;

      // 兼容开卡记录查询的接口参数结构
      if (pageKey === DistributorCenterPageKey.purchaseCard) {
        _variables.skip = _variables.options.skip;
        delete _variables.options;
      }

      if (this.searchResultConfig) {
        let res;

        if (pageKey === DistributorCenterPageKey.order) {
          res = await fetchDistributorsPromoteOrders(_variables);
        } else if (pageKey === DistributorCenterPageKey.product) {
          res = await fetchDistributorShareProducts(_variables);
        } else if (pageKey === DistributorCenterPageKey.customer) {
          res = await fetchDistributorBindingCustomers(_variables);
        } else if (pageKey === DistributorCenterPageKey.purchaseCard) {
          res = await fetchDistributorBindingMembers(_variables);
        }

        if (res?.items) {
          if (res.items.length < LIMIT) {
            this.searchResultConfig.loadStatus = LoadMoreStatus.noMore;
          } else {
            this.searchResultConfig.loadStatus = LoadMoreStatus.more;
          }
          if (refresh) {
            this.searchResultConfig.list = res.items;
          } else {
            this.searchResultConfig.list = [...this.searchResultConfig.list, ...res.items];
          }
        }

        return this.searchResultConfig.list;
      } else {
        console.log(`<searchResultConfig 初始化数据不存在>`);
      }
    },
  },
});
